// Contractele pure ale secțiunii `Scopuri și politici` (S4.3). Oglindesc
// `backend/ncrm/privacy/contracts/purpose-input.js` și `architecture.md §7.4`.

export const PURPOSE_CATEGORIES = Object.freeze(["marketing", "contract", "support", "recruitment", "other"]);
export const PURPOSE_CHANNELS = Object.freeze(["email", "phone", "sms", "whatsapp", "telegram", "postal"]);
export const LAWFUL_BASES = Object.freeze(["consent", "contract", "legal_obligation", "legitimate_interest"]);
export const UNKNOWN_POLICIES = Object.freeze(["allow", "warn", "review", "block"]);
export const PURPOSE_CODE_PATTERN = /^[a-z][a-z0-9_]{2,63}$/;

// Pe un scop activ, categoria, canalele și temeiul sunt identitatea lui istorică.
const ACTIVE_LOCKED = Object.freeze(["category", "channels", "default_lawful_basis_code"]);
const ALL_FIELDS = Object.freeze([
  "name", "description", "category", "channels", "default_lawful_basis_code",
  "unknown_policy", "double_opt_in_enabled", "show_in_contacts_summary",
]);

export const editableFields = (status) => {
  if (status === "retired") return [];
  if (status === "active") return ALL_FIELDS.filter((field) => !ACTIVE_LOCKED.includes(field));
  return [...ALL_FIELDS];
};

// Politica efectivă = override-ul scopului, altfel implicitul companiei.
export const effectivePolicy = (purpose, settings) => (purpose?.unknown_policy
  ? { value: purpose.unknown_policy, source: "purpose" }
  : { value: settings?.unknown_policy || null, source: "company" });

// Numai rute interne ale aplicației; orice altceva (URL absolut, `//host`) este ignorat.
export const safeReturnTo = (value) => {
  const path = typeof value === "string" ? value : "";
  return path.startsWith("/") && !path.startsWith("//") && !path.includes("\\") ? path : null;
};

// Deep-link către secțiunea canonică, cu întoarcere în contextul sursă.
export const purposesRoute = ({ returnTo, purposeId } = {}) => ({
  path: "/ncrm/data-protection",
  query: {
    section: "purposes",
    ...(purposeId ? { purpose: String(purposeId) } : {}),
    ...(safeReturnTo(returnTo) ? { returnTo } : {}),
  },
});

export const emptyPurposeForm = () => ({
  code: "", name: "", description: "", category: "marketing", channels: ["email"],
  default_lawful_basis_code: null, unknown_policy: null, double_opt_in_enabled: false, show_in_contacts_summary: true,
});

// Registrul întoarce flag-urile ca `0/1`; formularul lucrează cu booleene, altfel
// `0` față de `false` ar arăta o schimbare care nu există.
const BOOLEAN_FIELDS = Object.freeze(["double_opt_in_enabled", "show_in_contacts_summary"]);

export const purposeForm = (purpose) => ({
  ...emptyPurposeForm(),
  ...Object.fromEntries(Object.keys(emptyPurposeForm()).map((key) => [key, purpose[key] ?? emptyPurposeForm()[key]])),
  ...Object.fromEntries(BOOLEAN_FIELDS.map((key) => [key, Boolean(Number(purpose[key]))])),
  description: purpose.description || "",
  channels: [...(purpose.channels || [])],
});

// Numai câmpurile editabile care diferă de original ajung în PATCH.
export const purposeChanges = (original, form) => Object.fromEntries(editableFields(original.status)
  .filter((field) => JSON.stringify(normalize(field, form[field])) !== JSON.stringify(normalize(field, original[field])))
  .map((field) => [field, normalize(field, form[field])]));

const normalize = (field, value) => {
  if (BOOLEAN_FIELDS.includes(field)) return Boolean(Number(value));
  if (field === "channels") return [...(value || [])].sort();
  if (field === "description") return (value || "").trim() || null;
  if (field === "name") return (value || "").trim();
  return value ?? null;
};

export const createPayload = (form) => ({
  code: form.code.trim(),
  name: form.name.trim(),
  description: form.description.trim() || null,
  category: form.category,
  channels: [...form.channels],
  default_lawful_basis_code: form.default_lawful_basis_code,
  unknown_policy: form.unknown_policy,
  double_opt_in_enabled: form.double_opt_in_enabled,
  show_in_contacts_summary: form.show_in_contacts_summary,
});
